import { css } from 'aphrodite';
import moment from 'moment';
import * as React from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { Waypoint } from 'react-waypoint';
import { LoadingLogo } from '../../../web/components/LoadingLogo';
import { CalendarIcon } from '../../../web/components/svgs/icons/CalendarIcon';
import { GridIcon } from '../../../web/components/svgs/icons/GridIcon';
import { IconTabTrigger, TabContent, TabList, TabRoot } from '../../../web/components/Tabs';
import { EventCard } from '../../components/EventCard';
import { EventsNotFound } from '../../components/EventsNotFound';
import { useCustomPageStyle } from '../../hooks/useCustomPageStyle';
import { useEventsLanding } from '../../queries/useEventsLanding';
import { sendHeightToParent } from '../../utils/iframe';
import { EventsCalendar } from './EventsCalendar';
import { EventsCalendarList } from './EventsCalendarList';
import { styles } from './styles';

type ViewType = 'grid' | 'calendar';

const isViewType = (value: string | null): value is ViewType => value === 'grid' || value === 'calendar';

export const EventsLandingPage: React.FC = () => {
	const { shortCode } = useParams<{ shortCode: string }>();
	const location = useLocation();
	const { headerHtml, footerHtml, shouldShowBranding } = useCustomPageStyle(shortCode);

	// Initial view and past-events visibility are seeded from URL params so operators
	// can share links that open in a specific shape (e.g. ?view=calendar&showPast=true).
	// Defaults: grid view, upcoming events only.
	const initialState = React.useMemo(() => {
		const params = new URLSearchParams(location.search);
		const viewParam = params.get('view');
		return {
			view: isViewType(viewParam) ? viewParam : ('grid' as ViewType),
			showPast: params.get('showPast') === 'true',
		};
	}, [location.search]);

	const [activeView, setActiveView] = React.useState<ViewType>(initialState.view);
	const [showPastEvents] = React.useState(initialState.showPast);
	const [calendarMonth, setCalendarMonth] = React.useState(new Date());

	// Calculate date range for calendar view
	// For calendar view: fetch only events in the selected month
	// For grid view: fetch all events (no date range)
	// Padding by ±1 day accounts for timezone differences: a viewer in UTC+14
	// could have a different month boundary than the event's venue timezone,
	// so we fetch slightly outside the visible month to avoid missing edge events.
	const dateRange = React.useMemo(() => {
		if (activeView === 'calendar') {
			return {
				startDate: moment(calendarMonth).startOf('month').subtract(1, 'day').toDate(),
				endDate: moment(calendarMonth).endOf('month').add(1, 'day').toDate(),
			};
		}
		return {}; // No date range for grid view - fetch all events
	}, [activeView, calendarMonth]);

	// Fetch events with date range when in calendar view
	const eventsQuery = useEventsLanding({
		shortCode,
		...dateRange,
	});
	const {
		data: infiniteData,
		isLoading,
		isFetching,
		error,
		isError,
		hasNextPage,
		fetchNextPage,
		isFetchingNextPage,
	} = eventsQuery;

	// Flatten paginated results into single arrays
	const data = React.useMemo(() => {
		const pages = infiniteData?.pages;
		if (!pages?.length) return null;
		return {
			events: pages.flatMap(page => page.events),
			pastEvents: pages.flatMap(page => page.pastEvents),
			accountName: pages[0].accountName,
		};
	}, [infiniteData]);

	// In calendar view, auto-fetch all pages so the full month is visible.
	// Cap at 20 pages (1000 events) to prevent unbounded fetching.
	const MAX_CALENDAR_PAGES = 20;
	React.useEffect(() => {
		const pageCount = infiniteData?.pages?.length ?? 0;
		if (activeView === 'calendar' && hasNextPage && !isFetchingNextPage && !isError && pageCount < MAX_CALENDAR_PAGES) {
			fetchNextPage();
		}
	}, [activeView, hasNextPage, isFetchingNextPage, isError, fetchNextPage, infiniteData?.pages?.length]);

	// Track mobile vs desktop for responsive calendar
	const [isMobile, setIsMobile] = React.useState(() => {
		if (typeof window === 'undefined') return false;
		return window.matchMedia('(max-width: 767px)').matches;
	});

	React.useEffect(() => {
		if (typeof window === 'undefined') return;

		const mediaQuery = window.matchMedia('(max-width: 767px)');
		const handleChange = (e: MediaQueryListEvent) => setIsMobile(e.matches);

		mediaQuery.addEventListener('change', handleChange);
		return () => mediaQuery.removeEventListener('change', handleChange);
	}, []);

	// For calendar view: filter events based on checkbox (client-side filtering of fetched month)
	// For grid view: show current events always, past events only if checkbox is checked
	const visibleEvents = React.useMemo(() => {
		if (!data) return [];
		// In calendar view, combine all events and let checkbox filter past ones
		if (activeView === 'calendar') {
			return showPastEvents ? [...data.events, ...data.pastEvents] : data.events;
		}
		// In grid view, always show current events
		return data.events;
	}, [data, showPastEvents, activeView]);

	// Separate past events for grid view display
	const pastEventsToShow = React.useMemo(() => {
		if (!data || activeView !== 'grid' || !showPastEvents) return [];
		return data.pastEvents;
	}, [data, activeView, showPastEvents]);

	// Send height updates for iframe embedding
	// Poll every 500ms to detect content height changes for iframe resizing.
	// This interval balances responsiveness (detecting size changes quickly)
	// with performance (avoiding excessive postMessage calls). The 500ms
	// interval ensures smooth resizing when content loads or changes while
	// keeping overhead minimal for the parent frame.
	React.useEffect(() => {
		const interval = setInterval(() => {
			sendHeightToParent();
		}, 500);

		return () => clearInterval(interval);
	}, []);

	// Only show full-page loading on initial load when there's no data
	// With placeholderData: keepPreviousData, month navigation keeps previous data visible
	// so this condition is only true on the very first load
	if (isLoading && !data) {
		return (
			<div className={css(styles.loadingContainer)} role='status' aria-label='Loading events page'>
				<LoadingLogo size={100} />
			</div>
		);
	}

	if (error) {
		return (
			<div className={css(styles.errorContainer)}>
				<div className={css(styles.errorContent)}>
					<h1 className={css(styles.errorTitle)}>Unable to Load Events</h1>
					<p className={css(styles.errorMessage)}>
						We&apos;re having trouble loading events right now. Please try again later.
					</p>
				</div>
			</div>
		);
	}

	if (!data) {
		return <EventsNotFound />;
	}

	const hasNoEvents = visibleEvents.length === 0;

	return (
		<div className={css(styles.container)}>
			{/* Security Note: Custom header/footer HTML is controlled by account admins in the
			    main application and is sanitized before storage. Only authenticated account
			    owners can set these values, limiting XSS risk to self-inflicted harm. */}
			{shouldShowBranding && headerHtml && <div role='banner' dangerouslySetInnerHTML={{ __html: headerHtml }} />}

			<div className={css(styles.eventsSection)}>
				<TabRoot value={activeView} onValueChange={value => setActiveView(value as ViewType)}>
					<TabList styles={[styles.tabList]}>
						<IconTabTrigger value='grid' icon={<GridIcon width={16} height={16} />}>
							Grid View
						</IconTabTrigger>
						<IconTabTrigger value='calendar' icon={<CalendarIcon width={16} height={16} />}>
							Calendar View
						</IconTabTrigger>
					</TabList>

					{/* Grid View Tab */}
					<TabContent value='grid'>
						{hasNoEvents && pastEventsToShow.length === 0 ? (
							<EventsNotFound />
						) : (
							<>
								{/* Current/Upcoming Events */}
								{visibleEvents.length > 0 && (
									<div className={css(styles.eventsGrid)}>
										{visibleEvents.map(event => (
											<EventCard key={event.id} event={event} />
										))}
									</div>
								)}

								{/* Past Events Section - Only shown in grid view when checkbox is checked */}
								{pastEventsToShow.length > 0 && (
									<>
										<h2 className={css(styles.pastEventsHeading)}>Past Events</h2>
										<div className={css(styles.eventsGrid)}>
											{pastEventsToShow.map(event => (
												<EventCard key={event.id} event={event} />
											))}
										</div>
									</>
								)}

								{/* Show message if no current events but has past events */}
								{visibleEvents.length === 0 && pastEventsToShow.length > 0 && (
									<div className={css(styles.noCurrentEventsMessage)}>
										<p>No upcoming events at this time.</p>
									</div>
								)}

								{/* Infinite scroll trigger */}
								{!isFetchingNextPage && hasNextPage && (
									<Waypoint bottomOffset='-200px' onEnter={() => fetchNextPage()} />
								)}
								{isFetchingNextPage && (
									<div className={css(styles.loadingContainer)}>
										<LoadingLogo size={60} />
									</div>
								)}
							</>
						)}
					</TabContent>

					{/* Calendar View Tab */}
					<TabContent value='calendar'>
						{isMobile ? (
							<EventsCalendarList
								events={visibleEvents}
								month={calendarMonth}
								onMonthChange={setCalendarMonth}
								isLoading={isFetching}
							/>
						) : (
							<EventsCalendar
								events={visibleEvents}
								month={calendarMonth}
								onMonthChange={setCalendarMonth}
								isLoading={isFetching}
							/>
						)}
					</TabContent>
				</TabRoot>
			</div>

			{/* See security note above regarding custom HTML content */}
			{shouldShowBranding && footerHtml && <div role='contentinfo' dangerouslySetInnerHTML={{ __html: footerHtml }} />}
		</div>
	);
};
